<script setup>
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useRequest } from '@/composables/useRequest';
import ReportCard from '@/components/cards/ReportCard.vue';
defineProps({
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const selectedYear = ref(route.query.year || 'all');
const perPage = ref(5);

const getInitialFirst = () => {
  const pageQuery = parseInt(route.query.page);
  return pageQuery > 1 ? (pageQuery - 1) * perPage.value : 0;
};

const first = ref(getInitialFirst());
const currentPage = computed(() => Math.floor(first.value / perPage.value) + 1);

const buildRequestParams = () => {
  const params = {};

  if (currentPage.value > 1) params.page = currentPage.value;
  if (selectedYear.value !== 'all') params.year = selectedYear.value;

  return params;
};

const { data: response, pending } = await useAsyncData(
  'legal-list',
  () =>
    useRequest('legal', {
      params: buildRequestParams(),
    }),
  {
    watch: [currentPage, selectedYear],
  },
);
const results = computed(() => response.value?.items || []);
const totalRecords = computed(() => response.value?.count || 0);

const years = computed(() => {
  const apiYears = response.value?.years || [];
  if (!apiYears.length) return [];
  const sortedYears = [...apiYears].sort((a, b) => b - a);
  return [
    { label: t('reports.all_years'), value: 'all' },
    ...sortedYears.map((y) => ({
      label: y.toString(),
      value: y,
    })),
  ];
});
watch(
  () => response.value?.pagination?.per_page,
  (newPerPage) => {
    if (newPerPage) {
      const current = currentPage.value;
      perPage.value = newPerPage;
      first.value = (current - 1) * newPerPage;
    }
  },
  { immediate: true },
);
watch([currentPage, selectedYear], ([page, year]) => {
  const query = { ...route.query };

  if (page > 1) query.page = page.toString();
  else delete query.page;

  if (year !== 'all') query.year = year;
  else delete query.year;

  router.push({ query });
});

const onYearChange = () => {
  first.value = 0;
};

const lastPage = computed(() => Math.ceil(totalRecords.value / perPage.value));

const getPageQuery = (page) => {
  const query = { ...route.query };
  if (page > 1) query.page = page.toString();
  else delete query.page;
  return query;
};

const visiblePages = computed(() => {
  const current = currentPage.value;
  const last = lastPage.value;
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  let l;

  for (let i = 1; i <= last; i++) {
    if (
      i === 1 ||
      i === last ||
      (i >= current - delta && i <= current + delta)
    ) {
      range.push(i);
    }
  }

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

watch(
  () => route.query.page,
  (newPage) => {
    const p = parseInt(newPage) || 1;
    first.value = (p - 1) * perPage.value;
  },
);
</script>

<template>
  <div class="reports__list">
    <div
      v-if="pending"
      class="reports__overlay"
      :class="`reports__overlay--${backgroundColor || 'white'}`"
    >
      <div class="reports__overlay-spinner"></div>
    </div>
    <FloatLabel
      v-if="years && years.length > 0"
      class="reports__years floatlabel-ongray"
      variant="on"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
    >
      <Select
        v-model="selectedYear"
        :filter-placeholder="$t('reports.choose_year')"
        input-id="selectedYear"
        :options="years"
        option-label="label"
        option-value="value"
        variant="filled"
        class="select-ongray reports__years-select"
        overlay-class=""
        @change="onYearChange"
      >
        <template #dropdownicon>
          <CIcon
            name="direction-black"
            width="16"
            height="16"
            class="p-select-dropdown__icon"
          />
        </template>
      </Select>
      <label for="selectedYear" class="reports__years-label">{{
        $t('reports.choose_year')
      }}</label>
    </FloatLabel>
    <div class="reports__items">
      <template v-if="results.length > 0">
        <div
          v-for="(report, reportIndex) in results"
          :key="reportIndex"
          class="m-0 reports__item"
          data-aos="fade-up"
          :data-aos-delay="reportIndex >= 1 ? 100 + 100 * reportIndex : 100"
        >
          <ReportCard
            :title="report.title"
            :url="report.url"
            :date="report.date"
            :theme="backgroundColor"
          />
        </div>
      </template>
      <div v-else class="reports__items--empty">
        {{ $t('reports.empty') }}
      </div>
    </div>
    <nav
      v-if="lastPage > 1"
      class="reports__pagination"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
    >
      <NuxtLink
        :to="{ query: getPageQuery(currentPage - 1) }"
        class="reports__pagination-btn reports__pagination-btn--prev"
        :class="{ 'reports__pagination-btn--disabled': currentPage === 1 }"
        rel="prev"
      >
        <CIcon
          class="reports__pagination-btn-icon"
          name="direction-black"
          width="16"
          height="16"
        />
      </NuxtLink>

      <div class="reports__pagination-items">
        <template v-for="(page, idx) in visiblePages" :key="idx">
          <span
            v-if="page === '...'"
            class="reports__pagination-item reports__pagination-item--dots"
          >
            ...
          </span>
          <NuxtLink
            v-else
            :to="{ query: getPageQuery(page) }"
            :class="[
              'reports__pagination-item',
              { 'reports__pagination-item--active': page === currentPage },
            ]"
          >
            {{ page }}
          </NuxtLink>
        </template>
      </div>

      <NuxtLink
        :to="{ query: getPageQuery(currentPage + 1) }"
        class="reports__pagination-btn reports__pagination-btn--next"
        :class="{
          'reports__pagination-btn--disabled': currentPage === lastPage,
        }"
        rel="next"
      >
        <CIcon
          class="reports__pagination-btn-icon"
          name="direction-black"
          width="16"
          height="16"
        />
      </NuxtLink>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.reports {
  $parent: &;
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
    &-spinner {
      animation: loader-spin 0.8s linear infinite;
      border: 3px solid $divider;
      border-radius: 50%;
      border-top-color: $black;
      height: 40px;
      margin: 0 auto;
      position: sticky;
      top: calc(50vh - 20px);
      width: 40px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 3);
        @include responsive-spacing(height, 40);
        @include responsive-spacing(width, 40);
        top: calc(50vh - px-to-vw(20));
      }
    }
  }
  &__list {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 56px;
    position: relative;
  }
  &__years {
    margin: 0 auto;
    max-width: 240px;
    width: 100%;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(max-width, 240);
    }
  }
  &__items {
    list-style: none;
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 0;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(3, 1fr);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__pagination {
    align-items: center;
    display: flex;
    justify-content: center;
    gap: 12px;
    padding: 25px 16px 24px;
    @include media-breakpoint-up(sm) {
      gap: 28px;
      padding: 41px 48px 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 28);
      @include responsive-spacing(padding-top, 41);
      @include responsive-spacing(padding-left, 48);
      @include responsive-spacing(padding-right, 48);
      @include responsive-spacing(padding-bottom, 40);
    }
    &-items {
      align-items: center;
      display: flex;
      gap: 4px;
      list-style: none;
      margin: 0;
      padding: 0;
      @include media-breakpoint-up(sm) {
        gap: 12px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
      }
    }
    &-btn {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 32px;
      width: 32px;
      @include media-breakpoint-up(sm) {
        border: 1px solid $outline !important;
        border-radius: 50%;
        height: 48px;
        width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(width, 48);
      }
      &--disabled {
        pointer-events: none;
        opacity: 0.5;
      }
      &--prev {
        #{$parent} {
          &__pagination-btn {
            &-icon {
              transform: rotate(180deg);
            }
          }
        }
      }
    }
    &-item {
      margin: 0;
      padding: 0;
      @include link-normal;
      align-items: center;
      border-radius: 50%;
      display: inline-flex;
      justify-content: center;
      height: 32px;
      min-width: 24px;
      text-decoration: none;
      @include media-breakpoint-up(sm) {
        border: 1px solid $divider;
        height: 48px;
        min-width: 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-width, 1);
        @include responsive-spacing(height, 48);
        @include responsive-spacing(min-width, 48);
      }
      &--active {
        background-color: $black;
        border-radius: 50%;
        color: $white;
        min-width: 32px;
        @include media-breakpoint-up(sm) {
          border: 1px solid $black;
          min-width: 48px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-width, 1);
          @include responsive-spacing(min-width, 48);
        }
      }
      &--dots {
        @include link-normal;
        display: inline-flex;
        min-width: 24px;
        justify-content: center;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(min-width, 24);
        }
      }
    }
  }
}
</style>
